import {Injectable} from '@angular/core';
import {SettingsMenuItemType} from '../types/settings-types/settingsMenuItemType';
import {TYPE} from '../types/types';
import {GenericModelType} from '../types/dashboard-types/genericModelType';

@Injectable({
  providedIn: 'root',
})
export class ModuleConfigurationService {
  public readonly moduleConfiguration: TYPE.ModuleConfiguration[] = [
    {
      type: null,
      right: '',
      icon: '',
      color: 'default',
      orientation: 'topLeft',
      name: 'DASHBOARD',
      route: '/home',
      isLegacy: false,
      isNewsSource: false,
      adminOnly: false
    },
    {
      type: SettingsMenuItemType.News,
      right: 'intranet',
      icon: 'calendar_view_day',
      color: 'darkblue',
      orientation: 'bottomRight',
      name: 'NEWS',
      route: '/news',
      isLegacy: false,
      isNewsSource: true,
      adminOnly: false
    },
    {
      type: SettingsMenuItemType.Knowledgebase,
      right: 'mapp',
      icon: 'psychology',
      color: 'orange',
      orientation: 'topLeft',
      name: 'KNOWLEDGEBASE',
      route: '/knowledgebase',
      isLegacy: false,
      isNewsSource: true,
      adminOnly: false
    },
    {
      type: SettingsMenuItemType.Chat,
      right: 'chat',
      icon: 'chat',
      color: 'aqua',
      orientation: 'bottomLeft',
      name: 'CHAT',
      route: '/chat',
      isLegacy: false,
      isNewsSource: false,
      adminOnly: false
    },
    {
      type: SettingsMenuItemType.Communities,
      right: 'channel',
      icon: 'forum',
      color: 'yellow',
      orientation: 'topLeft',
      name: 'COMMUNITY',
      route: '/forum',
      isLegacy: true,
      isNewsSource: true,
      adminOnly: false
    },
    {
      type: SettingsMenuItemType.Meetings,
      right: 'meeting',
      icon: 'videocam',
      color: 'blue',
      orientation: 'bottomRight',
      name: 'MEETING',
      route: '/meeting',
      isLegacy: true,
      isNewsSource: false,
      adminOnly: false
    },
    {
      type: SettingsMenuItemType.Calendar,
      right: 'calendar',
      icon: 'calendar_month',
      color: 'asphalt',
      orientation: 'bottomRight',
      name: 'CALENDAR',
      route: '/calendar',
      isLegacy: false,
      isNewsSource: false,
      adminOnly: false
    },
    {
      type: SettingsMenuItemType.Feedback,
      right: 'survey',
      icon: 'cycle',
      color: 'lightpurple',
      orientation: 'topRight',
      name: 'FEEDBACK',
      route: '/survey/list',
      isLegacy: true,
      isNewsSource: false,
      adminOnly: false
    },
    {
      type: SettingsMenuItemType.Workflow,
      right: 'workflow',
      icon: 'rebase',
      color: 'pink',
      orientation: 'bottomLeft',
      name: 'WORKFLOW',
      route: '/workflow',
      isLegacy: true,
      isNewsSource: false,
      adminOnly: false
    },
    {
      type: SettingsMenuItemType.Tv,
      right: 'tv',
      icon: 'tv',
      color: 'cyan',
      orientation: 'bottomRight',
      name: 'TV',
      route: '/tv-admin',
      isLegacy: true,
      isNewsSource: false,
      adminOnly: false
    },
    {
      type: SettingsMenuItemType.Contacts,
      right: 'employee',
      icon: 'import_contacts',
      color: 'darkorange',
      orientation: 'topRight',
      name: 'CONTACTS',
      route: '/contacts',
      isLegacy: false,
      isNewsSource: false,
      adminOnly: false
    },
    {
      type: SettingsMenuItemType.Profile,
      right: 'profile',
      icon: 'perm_contact_calendar',
      color: 'purple',
      orientation: 'topRight',
      name: 'PROFILE',
      route: '/profile',
      isLegacy: false,
      isNewsSource: false,
      adminOnly: false
    },
    {
      type: SettingsMenuItemType.Settings,
      right: 'general',
      icon: 'settings',
      color: 'red',
      orientation: 'bottomLeft',
      name: 'SETTINGS',
      route: '/settings',
      isLegacy: false,
      isNewsSource: false,
      adminOnly: true
    },
  ];
  
  public readonly menuModules: TYPE.UserMenuItem[] = this.moduleConfiguration.filter((mod) => mod.type !== null) as TYPE.UserMenuItem[]; // filter out the dashboard itself which is not related to a menu item
  public readonly legacyModules: TYPE.ModuleConfiguration[] = this.moduleConfiguration.filter((mod) => mod.isLegacy);
  public readonly activeModules: TYPE.ModuleConfiguration[] = this.moduleConfiguration.filter((mod) => !mod.isLegacy);
  public readonly landingModules: TYPE.ModuleConfiguration[] = this.moduleConfiguration.filter((mod) => !mod.isLegacy && !mod.adminOnly);
  public readonly newsSourceModules: TYPE.ModuleConfiguration[] = this.moduleConfiguration.filter((mod) => mod.isNewsSource);

  public getRouteForLandingModule(module: string): string {
    if(module === 'dashboard') {
      return this.moduleConfiguration.find(m => m.name === 'DASHBOARD')?.route ?? '/home';
    } else {
      const foundModule = this.moduleConfiguration.find(m => m.type?.toLowerCase() === module.toLowerCase());
      return foundModule ? foundModule.route : '/home';
    }
  }

  public getRouteForNewsSource(type: SettingsMenuItemType, id: string, parent_id: string | undefined): string {
    if(type === SettingsMenuItemType.News) {
      return `/news/list/${id}`;
    } else if(type === SettingsMenuItemType.Knowledgebase) {
      if(!parent_id) {
        console.error('Cannot directly navigate to Knowledgebase, because parent_id is missing');
        return '/knowledgebase';
      } else {
        return `/knowledgebase/${parent_id}/${id}`;
      }
    } else if(type === SettingsMenuItemType.Communities) {
      if(!parent_id) {
        console.error('Cannot directly navigate to Communities, because parent_id is missing');
        return '/community';
      } else {
        console.log('TODO: open an article in a topic by url');
        return `/community/list/topic/${parent_id}`;
      }

    } else {
      return '';
    }
  }

  public getRouteForGenericModelResource(type: GenericModelType | undefined, id: string | null, parent_id: string | null): string | null {
    const normalizedId = this.normalizeRouteParam(id);
    const normalizedParentId = this.normalizeRouteParam(parent_id);

    if(type === GenericModelType.KnowledgebasePage) {
      if(!normalizedId) {
        console.error('Cannot directly navigate to Knowledgebase page, because id is missing');
        return '/knowledgebase';
      }
      return `/knowledgebase/${normalizedId}/0`;
    } else if(type === GenericModelType.KnowledgebaseContent) {
      if(!normalizedId || !normalizedParentId) {
        console.error('Cannot directly navigate to Knowledgebase content, because id or parent_id is missing');
        return '/knowledgebase';
      }
      return `/knowledgebase/${normalizedParentId}/${normalizedId}`;
    } else if(type === GenericModelType.Chat) {
      if(!normalizedId) {
        return '/chat';
      }
      return `/chat/detail/${normalizedId}/0`;
    } else if(type === GenericModelType.ChatMessage) {
      if(!normalizedParentId) {
        return '/chat';
      }
      return `/chat/detail/${normalizedParentId}/${normalizedId ?? 0}`;
    } else if(type === GenericModelType.News){
      if(!normalizedId) {
        console.error('Cannot directly navigate to News article, because id is missing');
        return '/news';
      }
      return `/news/list/${normalizedId}`;
    } else if(type === GenericModelType.CommunityTopic){
      if(!normalizedParentId) {
        console.error('Cannot directly navigate to Communities topic, because parent_id is missing');
        return '/community';
      }
      return `/community/list/topic/${normalizedParentId}`;
    } else if(type === GenericModelType.CalendarEvent){
      // currently we cannot open the exact date where the event is happening because the api does not let us resolve an event id to a date range to request
      return '/calendar/list'
    } else {
      return null;
    }
  }

  private normalizeRouteParam(value: string | null): string | null {
    if(value === null) {
      return null;
    }

    const trimmedValue = value.trim();

    if(!trimmedValue || trimmedValue === 'null' || trimmedValue === 'undefined') {
      return null;
    }

    return trimmedValue;
  }
}
